import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Camera, Coins, Star, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { COIN_REFRESH_EVENT } from "@/components/CoinBalanceChip";

const MAX_WORDS = 50;
const CATEGORIES = [{ v: "TRADE_REDEEM", l: "TRADE REDEEM" }, { v: "LUCKY_DRAW", l: "LUCKY DRAW" }];
const countWords = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

export default function WriteFeedback() {
  const [params] = useSearchParams();
  const source = params.get("source");
  const sourceId = params.get("id");
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [category, setCategory] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);
  const words = countWords(comment);

  useEffect(() => {
    api.get(`/member/feedback/eligibility/${source}/${sourceId}`)
      .then((res) => { if (res.data.feedback_id) navigate(`/member/feedback?highlight=${res.data.feedback_id}`, { replace: true }); else setInfo(res.data); })
      .catch((e) => setError(formatApiError(e)));
  }, [source, sourceId, navigate]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (submittingRef.current) return;
    if (!category) return toast.error("Please choose a category");
    if (!rating) return toast.error("Please select a star rating");
    if (!words) return toast.error("Please write your feedback");
    if (words > MAX_WORDS) return toast.error(`Feedback is ${words} words — the maximum is ${MAX_WORDS}`);
    submittingRef.current = true; setBusy(true);
    const fd = new FormData();
    fd.append("source", source); fd.append("source_id", sourceId); fd.append("category", category);
    fd.append("rating", String(rating)); fd.append("comment", comment.trim());
    if (file) fd.append("image", file);
    try {
      const { data } = await api.post("/member/feedback", fd);
      if (data.coins_awarded > 0) { window.dispatchEvent(new Event(COIN_REFRESH_EVENT)); toast.success("Feedback submitted — +1 PLUSMAKER coin credited"); }
      else if (data.coin_pending) toast.success("Feedback submitted. Coin credit is pending — it will be applied shortly.");
      else toast.success("Feedback submitted");
      navigate(`/member/feedback?highlight=${data.id}`, { replace: true });
    } catch (e) {
      if (e?.response?.status === 409) { toast.error("Feedback has already been submitted for this delivery"); navigate("/member/feedback", { replace: true }); }
      else toast.error(formatApiError(e));
      submittingRef.current = false; setBusy(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-5" data-testid="write-feedback-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-[#94A3B8] hover:text-white" data-testid="write-feedback-back" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="page-title">Write Feedback</h1>
      </div>
      {error && <div className="card-lux p-5 text-sm text-red-400" data-testid="write-feedback-error">{error}</div>}
      {info && (
        <>
          <div className="card-lux p-4 flex items-center gap-3" data-testid="write-feedback-reward">
            {info.reward_image && <img src={info.reward_image} alt="" className="h-14 w-14 rounded-lg object-cover border border-[#222530]" />}
            <p className="flex-1 text-sm font-medium text-white">{info.reward_name}</p>
            <span className="text-[9px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border text-emerald-400 bg-emerald-400/10 border-emerald-400/30">Delivered</span>
          </div>

          <div>
            <p className="text-sm text-white mb-2">Category <span className="text-[#D4AF37]">*</span></p>
            <div className="grid grid-cols-2 gap-3">
              {CATEGORIES.map((c) => (
                <button key={c.v} type="button" onClick={() => setCategory(c.v)} data-testid={`feedback-category-${c.v}`} aria-pressed={category === c.v}
                  className={`rounded-xl border py-3 text-xs font-mono tracking-widest ${category === c.v ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F5D77A]" : "border-[#2a2d38] text-[#94A3B8]"}`}>
                  {c.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm text-white mb-2">Your rating <span className="text-[#D4AF37]">*</span></p>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`feedback-star-${n}`} aria-label={`${n} star`} aria-pressed={rating >= n}>
                  <Star className={`h-9 w-9 ${rating >= n ? "text-[#F5C542] fill-[#F5C542]" : "text-[#3a3d4d]"}`} />
                </button>
              ))}
              <span className="ml-auto text-xs text-[#94A3B8] font-mono" data-testid="feedback-rating-value">{rating} / 5</span>
            </div>
          </div>

          <div>
            <p className="text-sm text-white mb-2">Add a photo <span className="text-[#64748B]">(optional)</span></p>
            <div className="flex gap-3">
              {preview && (
                <div className="relative h-28 w-28">
                  <img src={preview} alt="" className="h-28 w-28 rounded-xl object-cover border border-[#222530]" data-testid="feedback-image-preview" />
                  <button type="button" onClick={() => { setFile(null); setPreview(null); }} className="absolute -top-2 -right-2 rounded-full bg-black border border-[#2a2d38] p-1" data-testid="feedback-image-remove" aria-label="Remove photo"><X className="h-3 w-3 text-white" /></button>
                </div>
              )}
              <label className="h-28 w-28 rounded-xl border border-dashed border-[#2a2d38] flex flex-col items-center justify-center gap-1 text-xs text-[#94A3B8] cursor-pointer hover:border-[#D4AF37]/50">
                <Camera className="h-5 w-5" /> {preview ? "Change" : "Add photo"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="feedback-image-input" />
              </label>
            </div>
          </div>

          <div>
            <p className="text-sm text-white mb-2">Your feedback <span className="text-[#D4AF37]">*</span></p>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} data-testid="feedback-comment-input"
              className="w-full rounded-xl bg-[#0B0C10] border border-[#2a2d38] px-4 py-3 text-sm text-white focus:outline-none focus:border-[#D4AF37]/60" placeholder="Share your experience…" />
            <div className="flex justify-between mt-1.5 text-xs">
              <span className="text-[#64748B]">Maximum {MAX_WORDS} words</span>
              <span className={words > MAX_WORDS ? "text-red-400 font-semibold" : "text-[#94A3B8]"} data-testid="feedback-word-count">{words} / {MAX_WORDS} words</span>
            </div>
          </div>

          <p className="flex items-center gap-2 text-xs text-[#D4AF37]" data-testid="feedback-coin-note"><Coins className="h-4 w-4" /> 5-star feedback: +1 coin</p>
          <button onClick={submit} disabled={busy} className="btn-gold w-full py-3.5 text-sm disabled:opacity-60" data-testid="feedback-submit">
            {busy ? "Submitting…" : "Submit Feedback"}
          </button>
        </>
      )}
    </div>
  );
}
